import { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Table, Form, Alert, Spinner, Modal, Badge, ButtonGroup } from 'react-bootstrap'
import { api } from '../../services/api'

interface EmailTemplate {
  id: string
  name: string
  subject: string
  body: string
  type: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  _count: {
    sent: number
  }
}

interface TemplateData {
  name: string
  subject: string
  body: string
  type: string
}

const templateTypes = [
  { value: 'ticket_created', label: 'Ticket Created' },
  { value: 'ticket_updated', label: 'Ticket Updated' },
  { value: 'ticket_assigned', label: 'Ticket Assigned' },
  { value: 'ticket_resolved', label: 'Ticket Resolved' },
  { value: 'ticket_closed', label: 'Ticket Closed' },
  { value: 'comment_added', label: 'Comment Added' },
  { value: 'user_invited', label: 'User Invited' },
  { value: 'password_reset', label: 'Password Reset' },
  { value: 'welcome', label: 'Welcome' }
]

export const EmailTemplatesPage = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)
  const [templateData, setTemplateData] = useState<TemplateData>({
    name: '',
    subject: '',
    body: '',
    type: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [previewMode, setPreviewMode] = useState(false)

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      setLoading(true)
      const response = await api.get('/admin/email-templates')
      setTemplates(response.data.data || [])
    } catch (err: any) {
      setError('Failed to load email templates')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      if (editingTemplate) {
        await api.patch(`/admin/email-templates/${editingTemplate.id}`, templateData)
      } else {
        await api.post('/admin/email-templates', templateData)
      }
      setShowModal(false)
      setEditingTemplate(null)
      setTemplateData({ name: '', subject: '', body: '', type: '' })
      fetchTemplates()
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save email template')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template)
    setTemplateData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      type: template.type
    })
    setShowModal(true)
  }

  const handleDelete = async (templateId: string) => {
    if (window.confirm('Are you sure you want to delete this email template?')) {
      try {
        await api.delete(`/admin/email-templates/${templateId}`)
        fetchTemplates()
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Failed to delete email template')
      }
    }
  }

  const handleToggleStatus = async (templateId: string, isActive: boolean) => {
    try {
      await api.patch(`/admin/email-templates/${templateId}`, { isActive: !isActive })
      fetchTemplates()
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update email template')
    }
  }

  // Preview mode controlled via state; toggler removed if unused

  const handleTestEmail = async (templateId: string) => {
    try {
      await api.post(`/admin/email-templates/${templateId}/test`)
      // Show success message
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to send test email')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'ticket_created': return 'primary'
      case 'ticket_updated': return 'info'
      case 'ticket_assigned': return 'warning'
      case 'ticket_resolved': return 'success'
      case 'ticket_closed': return 'secondary'
      case 'comment_added': return 'info'
      case 'user_invited': return 'success'
      case 'password_reset': return 'warning'
      case 'welcome': return 'primary'
      default: return 'secondary'
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div className="email-templates-page">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
        <div className="mb-2 mb-sm-0">
          <h2 className="mb-1">Email Templates</h2>
          <p className="text-muted mb-0 small">Manage automated email notifications and communications</p>
        </div>
        <Button variant="primary" onClick={() => setShowModal(true)} className="d-flex align-items-center">
          <i className="fas fa-plus me-2"></i>
          Add Template
        </Button>
      </div>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-primary">{templates.length}</h3>
              <p className="text-muted mb-0">Total Templates</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-success">{templates.filter(t => t.isActive).length}</h3>
              <p className="text-muted mb-0">Active Templates</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-info">{templates.reduce((sum, t) => sum + t._count.sent, 0)}</h3>
              <p className="text-muted mb-0">Emails Sent</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-warning">
                {templates.length > 0 ? Math.round(templates.reduce((sum, t) => sum + t._count.sent, 0) / templates.length) : 0}
              </h3>
              <p className="text-muted mb-0">Avg Sent</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Email Templates Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <h5 className="mb-0">
            <i className="fas fa-envelope me-2 text-primary"></i>
            Email Templates ({templates.length})
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          )}
          
          {templates.length > 0 ? (
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Template</th>
                  <th>Type</th>
                  <th>Subject</th>
                  <th>Emails Sent</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td>
                      <div>
                        <div className="fw-medium">{template.name}</div>
                        <small className="text-muted">
                          {template.body.substring(0, 50)}
                          {template.body.length > 50 && '...'}
                        </small>
                      </div>
                    </td>
                    <td>
                      <Badge bg={getTypeColor(template.type)}>
                        {template.type.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </td>
                    <td>
                      <div className="text-truncate" style={{ maxWidth: '200px' }}>
                        {template.subject}
                      </div>
                    </td>
                    <td>
                      <Badge bg="info">{template._count.sent}</Badge>
                    </td>
                    <td>
                      <Badge bg={template.isActive ? 'success' : 'danger'}>
                        {template.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <span className="text-muted">
                        {formatDate(template.updatedAt)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleEdit(template)}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button 
                          variant="outline-info" 
                          size="sm"
                          onClick={() => handleTestEmail(template.id)}
                        >
                          <i className="fas fa-paper-plane"></i>
                        </Button>
                        <Button 
                          variant={template.isActive ? "outline-warning" : "outline-success"} 
                          size="sm"
                          onClick={() => handleToggleStatus(template.id, template.isActive)}
                        >
                          <i className={`fas fa-${template.isActive ? 'ban' : 'check'}`}></i>
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDelete(template.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-envelope text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3 text-muted">No email templates found</h5>
              <p className="text-muted">Create your first email template using the button above</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Email Template Modal */}
      <Modal show={showModal} onHide={() => {
        setShowModal(false)
        setEditingTemplate(null)
        setTemplateData({ name: '', subject: '', body: '', type: '' })
        setPreviewMode(false)
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTemplate ? 'Edit Email Template' : 'Add New Email Template'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Template Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={templateData.name}
                    onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    placeholder="e.g., Ticket Created Notification"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Template Type</Form.Label>
                  <Form.Select
                    value={templateData.type}
                    onChange={(e) => setTemplateData(prev => ({ ...prev, type: e.target.value }))}
                    required
                  >
                    <option value="">Select Type</option>
                    {templateTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Subject Line</Form.Label>
              <Form.Control
                type="text"
                value={templateData.subject}
                onChange={(e) => setTemplateData(prev => ({ ...prev, subject: e.target.value }))}
                required
                placeholder="e.g., New Ticket: {{ticket.subject}}"
              />
              <Form.Text className="text-muted">
                Use variables like {`{{ticket.subject}}, {{user.name}}, {{ticket.id}}`}
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email Body</Form.Label>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>Template Content</span>
                <ButtonGroup size="sm">
                  <Button 
                    variant={!previewMode ? "primary" : "outline-primary"}
                    onClick={() => setPreviewMode(false)}
                  >
                    Edit
                  </Button>
                  <Button 
                    variant={previewMode ? "primary" : "outline-primary"}
                    onClick={() => setPreviewMode(true)}
                  >
                    Preview
                  </Button>
                </ButtonGroup>
              </div>
              
              {previewMode ? (
                <div 
                  className="border rounded p-3" 
                  style={{ 
                    minHeight: '300px', 
                    backgroundColor: '#f8f9fa',
                    whiteSpace: 'pre-wrap'
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: templateData.body.replace(/\n/g, '<br>') 
                  }}
                />
              ) : (
                <Form.Control
                  as="textarea"
                  rows={12}
                  value={templateData.body}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, body: e.target.value }))}
                  required
                  placeholder="Enter your email template content here..."
                />
              )}
              
              <Form.Text className="text-muted">
                Use HTML tags for formatting. Available variables: {`{{ticket.subject}}, {{ticket.description}}, {{user.name}}, {{user.email}}, {{ticket.id}}, {{ticket.status}}`}
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowModal(false)
            setEditingTemplate(null)
            setTemplateData({ name: '', subject: '', body: '', type: '' })
            setPreviewMode(false)
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={submitting || !templateData.name.trim() || !templateData.subject.trim() || !templateData.body.trim() || !templateData.type}
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {editingTemplate ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                {editingTemplate ? 'Update Template' : 'Create Template'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
